// ELS MISSATGES D'ERROR SÓN CLAUS, no text.
//
// Un thunk no pot cridar `t()` —no és dins de cap component— i per això aquí hi havia text
// anglès escrit al codi, que **es veia a la capçalera de l'editor** encara que l'aplicació
// anés en català. Ara el que viatja és la clau i qui la pinta la tradueix.
//
// **El que això NO arregla**: quan l'API sí que envia un missatge, aquell missatge és seu i
// també és en anglès. Arreglar-ho de debò vol dir que el servidor enviï codis, com ja fa
// amb `reason: 'pending'` de l'autenticació. Queda pendent.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../services/authService';

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await authService.login(email, password);
      return response;
    } catch (error) {
      // Es retorna el cos sencer, no només el text. El servidor hi posa un `reason`
      // —'pending', 'rejected', 'inactive'— i la pantalla d'entrada l'ha de poder distingir
      // d'una contrasenya equivocada: no és el mateix error ni s'arregla igual.
      return rejectWithValue(error.response?.data || { error: 'errors.login' });
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.register');
    }
  }
);

export const checkAuth = createAsyncThunk(
  'auth/check',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) throw new Error('No token');
      const response = await authService.getProfile();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.removeItem('token');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        localStorage.setItem('token', action.payload.token);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        // `state.error` continua sent text: qui el pinta no ha de saber la forma del payload.
        state.error = action.payload?.error || action.payload || 'errors.login';
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Registrar-se ja NO és entrar. Des del 24/08/2026 el servidor contesta 202 sense cap
      // testimoni: el compte queda pendent que un admin l'aprovi. Abans aquí s'hi feia
      // `localStorage.setItem('token', action.payload.token)` amb un `token` que ara no ve mai,
      // de manera que hi hauria quedat desada la cadena "undefined" i cada petició següent
      // hauria sortit amb una capçalera `Bearer undefined`.
      .addCase(register.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem('token');
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;